import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Typography from '../../components/Typography';
import OverviewCustomCard1 from '../../components/OverviewCustomCard1';
import admin_avatar from '../../images/admin_avatar.png';
import { useNavigate, useParams } from 'react-router-dom';

const Accounts = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [users, setUsers] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [totalInvestments, setTotalInvestments] = useState(0);
  const [investmentReturns, setInvestmentReturns] = useState(0);
  const [averageROI, setAverageROI] = useState(0);
  const [openIndex, setOpenIndex] = useState(null);
  const [sortOption, setSortOption] = useState('Newest');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const formatDate = (dateString) => {
    if (!dateString) return '[Insert Date]';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Function to format large numbers into K, M, etc.
  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(3)}M`;
    if (value >= 1000) {
      const thousands = value / 1000;
      return thousands % 1 === 0 ? `$${thousands}K` : `$${(value / 1000).toFixed(2)}K`;
    }
    return `$${value.toLocaleString()}`;
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/admin`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers(response.data.users);
        setInvestments(response.data.investments);
        setTickets(response.data.tickets);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, []);

  // Calculate Total Investments, Investment Returns, and Average ROI
  useEffect(() => {
    if (investments.length > 0) {
      // Total Investments: Sum of initialInvestment
      const totalInv = investments.reduce((sum, inv) => sum + (inv.initialInvestment || 0), 0);
      setTotalInvestments(totalInv);

      // Investment Returns: Sum of all payouts
      const totalReturns = investments.reduce((sum, inv) => {
        const payouts = inv.payouts || [];
        const payoutSum = payouts.reduce((pSum, payout) => pSum + (payout.amount || 0), 0);
        return sum + payoutSum;
      }, 0);
      setInvestmentReturns(totalReturns);

      // Average ROI: Average of annualROI
      const avgROI = investments.length > 0
        ? (investments.reduce((sum, inv) => sum + (inv.annualROI || 0), 0) / investments.length).toFixed(1)
        : 0;
      setAverageROI(avgROI);
    }
  }, [investments]);

  const sortUsers = (usersList) => {
    console.log(usersList);
    return [...usersList].sort((a, b) => {
      const aInvestments = investments.filter((inv) => inv.userId === a._id).reduce((sum, inv) => sum + (inv.initialInvestment || 0), 0);
      const bInvestments = investments.filter((inv) => inv.userId === b._id).reduce((sum, inv) => sum + (inv.initialInvestment || 0), 0);
      const aDate = new Date(a.createdAt);
      const bDate = new Date(b.createdAt);

      switch (sortOption) {
        case 'Newest':
          return bDate - aDate;
        case 'Oldest':
          return aDate - bDate;
        case 'Top Amount':
          return bInvestments - aInvestments;
        case 'Low Amount':
          return aInvestments - bInvestments;
        default:
          return bDate - aDate; // Default to Newest
      }
    });
  };

  useEffect(() => {
    if (id && users.length > 0) {
      const index = users.findIndex(user => user._id === id);
      setActiveIndex(index !== -1 ? index : 0);
    }
  }, [id, users]);

  return (
    <>
      <div className="w-full md:p-5 p-4 rounded-[4px] bg-black">
        <Typography text={'OVERVIEW'} transform={'uppercase'} color={'#FFF'} size={'16px'} weight={400} lineheight={'170%'} family={'Supply'} />
        <div className='mt-[25px] flex sm:gap-[10px] gap-1 sm:justify-between justify-center sm:flex-nowrap flex-wrap'>
          <OverviewCustomCard1
            icon={<svg xmlns="http://www.w3.org/2000/svg" className='lg:w-[60px] lg:h-[60px] md:w-[50px] md:h-[50px] w-[40px] h-[40px]' width="60" height="60" viewBox="0 0 60 60" fill="none">
              <g clipPath="url(#clip0_498_705)">
                <path opacity="0.2" d="M30.0014 7.5C25.4496 7.49973 21.0049 8.87997 17.254 11.4585C13.503 14.037 10.6224 17.6925 8.9923 21.9423C7.36224 26.1921 7.05946 30.8364 8.12394 35.2619C9.18842 39.6874 11.5701 43.686 14.9545 46.7297C16.3661 43.9521 18.519 41.6196 21.1749 39.9905C23.8307 38.3614 26.8857 37.4994 30.0014 37.5C28.1472 37.5 26.3346 36.9502 24.7929 35.92C23.2512 34.8899 22.0496 33.4257 21.34 31.7127C20.6304 29.9996 20.4448 28.1146 20.8065 26.296C21.1682 24.4775 22.0611 22.807 23.3722 21.4959C24.6834 20.1848 26.3538 19.2919 28.1724 18.9301C29.991 18.5684 31.876 18.7541 33.589 19.4636C35.3021 20.1732 36.7663 21.3748 37.7964 22.9165C38.8265 24.4582 39.3764 26.2708 39.3764 28.125C39.3764 30.6114 38.3886 32.996 36.6305 34.7541C34.8723 36.5123 32.4878 37.5 30.0014 37.5C33.1168 37.4992 36.1716 38.3609 38.8274 39.9895C41.4832 41.6182 43.6363 43.9502 45.0482 46.7273C48.4318 43.6835 50.8127 39.6851 51.8766 35.26C52.9406 30.8349 52.6376 26.1912 51.0077 21.9419C49.3777 17.6926 46.4974 14.0375 42.7471 11.4591C38.9967 8.88069 34.5526 7.50025 30.0014 7.5Z" fill="white" />
                <path d="M14.9531 46.7273C16.3639 43.9486 18.5165 41.6148 21.1724 39.9846C23.8283 38.3544 26.8837 37.4915 30 37.4915C33.1163 37.4915 36.1717 38.3544 38.8276 39.9846C41.4835 41.6148 43.6361 43.9486 45.0469 46.7273" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M30 52.5C42.4264 52.5 52.5 42.4264 52.5 30C52.5 17.5736 42.4264 7.5 30 7.5C17.5736 7.5 7.5 17.5736 7.5 30C7.5 42.4264 17.5736 52.5 30 52.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M30 37.5C35.1777 37.5 39.375 33.3027 39.375 28.125C39.375 22.9473 35.1777 18.75 30 18.75C24.8223 18.75 20.625 22.9473 20.625 28.125C20.625 33.3027 24.8223 37.5 30 37.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_498_705">
                  <rect width="60" height="60" fill="white" />
                </clipPath>
              </defs>
            </svg>}
            title={'Registered users'}
            value={users.length ?? 0}
          />
          <OverviewCustomCard1
            icon={<svg xmlns="http://www.w3.org/2000/svg" className='lg:w-[60px] lg:h-[60px] md:w-[50px] md:h-[50px] w-[40px] h-[40px]' width="60" height="60" viewBox="0 0 60 60" fill="none">
              <g clipPath="url(#clip0_498_950)">
                <path opacity="0.2" d="M41.2498 22.6688V29.0625C41.2498 33.2813 34.3967 36.7617 25.4365 37.3969C28.6967 38.632 32.9061 39.375 37.4998 39.375C47.8545 39.375 56.2498 35.5969 56.2498 30.9375C56.2498 26.8547 49.8092 23.4516 41.2498 22.6688Z" fill="white" />
                <path opacity="0.2" d="M22.5 28.125C32.8553 28.125 41.25 24.3474 41.25 19.6875C41.25 15.0276 32.8553 11.25 22.5 11.25C12.1447 11.25 3.75 15.0276 3.75 19.6875C3.75 24.3474 12.1447 28.125 22.5 28.125Z" fill="white" />
                <path d="M22.5 28.125C32.8553 28.125 41.25 24.3474 41.25 19.6875C41.25 15.0276 32.8553 11.25 22.5 11.25C12.1447 11.25 3.75 15.0276 3.75 19.6875C3.75 24.3474 12.1447 28.125 22.5 28.125Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M3.75 19.6875V29.0625C3.75 33.7219 12.1453 37.5 22.5 37.5C32.8547 37.5 41.25 33.7219 41.25 29.0625V19.6875" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M15 27.4219V36.7969" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M41.2498 22.6688C49.8092 23.4516 56.2498 26.8547 56.2498 30.9375C56.2498 35.5969 47.8545 39.375 37.4998 39.375C32.9061 39.375 28.6967 38.632 25.4365 37.3969" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M18.75 37.3312V40.3125C18.75 44.9719 27.1453 48.75 37.5 48.75C47.8547 48.75 56.25 44.9719 56.25 40.3125V30.9375" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M45 38.6719V48.0469" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M30 27.4219V48.0469" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_498_950">
                  <rect width="60" height="60" fill="white" />
                </clipPath>
              </defs>
            </svg>}
            title={'Total Investments'}
            value={formatCurrency(totalInvestments)}
          />
          <OverviewCustomCard1
            icon={<svg xmlns="http://www.w3.org/2000/svg" className='lg:w-[60px] lg:h-[60px] md:w-[50px] md:h-[50px] w-[40px] h-[40px]' width="60" height="60" viewBox="0 0 60 60" fill="none">
              <g clipPath="url(#clip0_498_961)">
                <path opacity="0.2" d="M7.5 11.25H48.75C49.7446 11.25 50.6984 11.6451 51.4016 12.3483C52.1049 13.0516 52.5 14.0054 52.5 15V48.75H7.5V11.25Z" fill="white" />
                <path d="M52.5 48.75H7.5V11.25" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M46.875 16.875L30 33.75L22.5 26.25L7.5 41.25" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M46.875 26.25V16.875H37.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_498_961">
                  <rect width="60" height="60" fill="white" />
                </clipPath>
              </defs>
            </svg>}
            title={'Investment Returns'}
            value={formatCurrency(investmentReturns)}
          />
          <OverviewCustomCard1
            icon={<svg xmlns="http://www.w3.org/2000/svg" className='lg:w-[60px] lg:h-[60px] md:w-[50px] md:h-[50px] w-[40px] h-[40px]' width="60" height="60" viewBox="0 0 60 60" fill="none">
              <g clipPath="url(#clip0_498_967)">
                <path opacity="0.2" d="M12.7641 47.2359C10.6078 45.0797 12.0375 40.5492 10.9406 37.8961C9.80156 35.1562 5.625 32.9297 5.625 30C5.625 27.0703 9.80156 24.8438 10.9406 22.1039C12.0375 19.4531 10.6078 14.9203 12.7641 12.7641C14.9203 10.6078 19.4531 12.0375 22.1039 10.9406C24.8555 9.80156 27.0703 5.625 30 5.625C32.9297 5.625 35.1562 9.80156 37.8961 10.9406C40.5492 12.0375 45.0797 10.6078 47.2359 12.7641C49.3922 14.9203 47.9625 19.4508 49.0594 22.1039C50.1984 24.8555 54.375 27.0703 54.375 30C54.375 32.9297 50.1984 35.1562 49.0594 37.8961C47.9625 40.5492 49.3922 45.0797 47.2359 47.2359C45.0797 49.3922 40.5492 47.9625 37.8961 49.0594C35.1562 50.1984 32.9297 54.375 30 54.375C27.0703 54.375 24.8438 50.1984 22.1039 49.0594C19.4531 47.9625 14.9203 49.3922 12.7641 47.2359Z" fill="white" />
                <path d="M12.7641 47.2359C10.6078 45.0797 12.0375 40.5492 10.9406 37.8961C9.80156 35.1563 5.625 32.9297 5.625 30C5.625 27.0703 9.80156 24.8437 10.9406 22.1039C12.0375 19.4531 10.6078 14.9203 12.7641 12.7641C14.9203 10.6078 19.4531 12.0375 22.1039 10.9406C24.8555 9.80156 27.0703 5.625 30 5.625C32.9297 5.625 35.1563 9.80156 37.8961 10.9406C40.5492 12.0375 45.0797 10.6078 47.2359 12.7641C49.3922 14.9203 47.9625 19.4508 49.0594 22.1039C50.1984 24.8555 54.375 27.0703 54.375 30C54.375 32.9297 50.1984 35.1563 49.0594 37.8961C47.9625 40.5492 49.3922 45.0797 47.2359 47.2359C45.0797 49.3922 40.5492 47.9625 37.8961 49.0594C35.1563 50.1984 32.9297 54.375 30 54.375C27.0703 54.375 24.8437 50.1984 22.1039 49.0594C19.4531 47.9625 14.9203 49.3922 12.7641 47.2359Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M22.5 26.25C24.5711 26.25 26.25 24.5711 26.25 22.5C26.25 20.4289 24.5711 18.75 22.5 18.75C20.4289 18.75 18.75 20.4289 18.75 22.5C18.75 24.5711 20.4289 26.25 22.5 26.25Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M37.5 41.25C39.5711 41.25 41.25 39.5711 41.25 37.5C41.25 35.4289 39.5711 33.75 37.5 33.75C35.4289 33.75 33.75 35.4289 33.75 37.5C33.75 39.5711 35.4289 41.25 37.5 41.25Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M20.625 39.375L39.375 20.625" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_498_967">
                  <rect width="60" height="60" fill="white" />
                </clipPath>
              </defs>
            </svg>}
            title={'Average ROI'}
            value={`${averageROI}%`}
          />
        </div>
      </div>
      <div className="w-full mt-[14px] lg:p-5 rounded-[4px] lg:bg-black">
        <div className="lg:grid flex lg:gap-0 gap-4 flex-col-reverse  lg:grid-cols-12">
          <div className="lg:col-span-8 bg-black lg:p-0 p-5 rounded-[4px] col-span-12 lg:pr-8 lg:border-r border-opacity-20 border-white">
            <div className="flex justify-between items-center">
              <Typography text={'YOUR INVESTORS'} transform={'uppercase'} color={'#FFF'} size={'16px'} weight={400} lineheight={'170%'} family={'Supply'} />
              <div className="relative">
                <div
                  className="flex cursor-pointer justify-between gap-2.5 rounded border border-white items-center h-8 w-32 p-[10px]"
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-[14px] h-[14px] flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <mask id="mask0_498_834" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="14" height="14">
                          <rect width="14" height="14" fill="#D9D9D9" />
                        </mask>
                        <g mask="url(#mask0_498_834)">
                          <path d="M4.90233 7.38136V3.13426L3.24873 4.78786L2.625 4.17303L5.33969 1.45834L8.05452 4.17303L7.43079 4.78786L5.77719 3.13426V7.38136H4.90233ZM8.65463 12.5417L5.93994 9.82699L6.56367 9.21216L8.21713 10.8658V6.61866H9.09213V10.8658L10.7457 9.21216L11.3693 9.82699L8.65463 12.5417Z" fill="white" />
                        </g>
                      </svg>
                    </div>
                    <Typography color={'#FFF'} size={'12px'} lineheight={'170%'} weight={400} text={'Sort By'} />
                  </div>
                  <div className="w-[10px] h-[10px] flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <mask id="mask0_498_838" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="10" height="10">
                        <rect x="10" width="10" height="10" transform="rotate(90 10 0)" fill="#D9D9D9" />
                      </mask>
                      <g mask="url(#mask0_498_838)">
                        <path d="M0.834635 3.3431L1.57422 2.60352L5.0013 6.0306L8.42839 2.60352L9.16797 3.3431L5.0013 7.50977L0.834635 3.3431Z" fill="white" />
                      </g>
                    </svg>
                  </div>
                </div>
                {showSortDropdown && (
                  <div className="absolute right-0 w-32 bg-black border border-white rounded shadow-lg z-10">
                    {['Newest', 'Oldest', 'Top Amount', 'Low Amount'].map((option) => (
                      <div
                        key={option}
                        className="px-2 py-2 text-white cursor-pointer"
                        onClick={() => {
                          setSortOption(option);
                          setShowSortDropdown(false);
                        }}
                      >
                        <Typography color={'#FFF'} size={'12px'} lineheight={'170%'} weight={400} text={option} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="relative md:hidden block overflow-x-auto mt-3">
              {(sortUsers(users) || []).map((user, index) => {
                const userInvestments = investments?.filter((inv) => inv.userId === user._id) || [];
                const totalUserInvestment = userInvestments.reduce((sum, inv) => sum + (inv.initialInvestment || 0), 0);
                const totalUserPayout = userInvestments.reduce((sum, inv) => {
                  const payouts = inv.payouts || [];
                  return sum + payouts.reduce((pSum, payout) => pSum + (payout.amount || 0), 0);
                }, 0);
                const period = userInvestments.length > 0 ? userInvestments[0].term : 12; // Default to 12 months if no investments
                return (
                  <div
                    key={index}
                    className="border-b flex-col flex items-start border-gray-700 py-5 cursor-pointer"
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  >
                    <div className="flex flex-col  items-start self-stretch">
                      {/* <Typography text={ticket.ticketNo} opacity={0.6} size={'14px'} color='#FFF' /> */}
                      <div className="flex justify-between items-center self-stretch">
                        <div className='flex items-center gap-[6px] '>
                          <img className="w-[26px] h-[26px] flex-shrink-0 rounded-[26px]" src={user?.avatar && user?.avatar !== '' ?  process.env.REACT_APP_BACKEND_URL+user?.avatar : admin_avatar} alt="admin_avatar" />
                          <Typography text={user.name} size={'14px'} weight={400} color='#FFF' />
                        </div>
                        {openIndex === index ? <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <mask id="mask0_4672_17113" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="14" height="14">
                            <rect width="14" height="14" transform="matrix(-4.37114e-08 1 1 4.37114e-08 0 3.05176e-05)" fill="#D9D9D9" />
                          </mask>
                          <g mask="url(#mask0_4672_17113)">
                            <path d="M12.6318 4.67558L11.804 3.84769L7.00049 8.65115L2.19704 3.84769L1.36914 4.67558L7.00049 10.3069L12.6318 4.67558Z" fill="#DF0F11" />
                          </g>
                        </svg> : <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <mask id="mask0_4672_17226" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="14" height="14">
                            <rect width="14" height="14" transform="matrix(1.19249e-08 -1 -1 -1.19249e-08 14 14.0001)" fill="#D9D9D9" />
                          </mask>
                          <g mask="url(#mask0_4672_17226)">
                            <path d="M1.36815 9.32454L2.19605 10.1524L6.99951 5.34898L11.803 10.1524L12.6309 9.32454L6.99951 3.69318L1.36815 9.32454Z" fill="#DF0F11" />
                          </g>
                        </svg>}
                      </div>
                    </div>

                    {/* Optional expanded content */}
                    {openIndex === index && (
                      <div className="flex mt-4 translate-y-0 flex-col gap-5 justify-center items-start self-stretch animation transition-all animate-backdropFadeIn">
                        <div className="flex justify-between items-start self-stretch">
                          <Typography text={'User Id'} opacity={0.6} size={'12px'} color='#FFF' />
                          <Typography text={user._id} size={'14px'} color='#FFF' />
                        </div>
                        <div className="flex justify-between items-start self-stretch">
                          <Typography text={'Investments'} opacity={0.6} size={'12px'} color='#FFF' />
                          <Typography text={userInvestments.length} size={'14px'} color='#FFF' />
                        </div>
                        <div className="flex justify-between items-start self-stretch">
                          <Typography text={'Period'} opacity={0.6} size={'12px'} color='#FFF' />
                          <Typography text={`${period} ${period === 1 ? 'Month' : 'Months'}`} size={'14px'} color='#FFF' />
                        </div>
                        <div className="flex justify-between items-start self-stretch">
                          <Typography text={'Amount'} opacity={0.6} size={'12px'} color='#FFF' />
                          <Typography text={formatCurrency(totalUserInvestment)} size={'14px'} color='#FFF' />
                        </div>
                        <div className="flex justify-between items-start self-stretch">
                          <Typography text={'Payout'} opacity={0.6} size={'12px'} color='#FFF' />
                          <Typography text={formatCurrency(totalUserPayout)} size={'14px'} color='#FFF' />
                        </div>
                      </div>
                    )}
                  </div>)
              })}
            </div>
            <div className="relative md:block hidden overflow-x-auto mt-3">
              <table className="w-full text-sm text-left rtl:text-right table-fixed">
                <thead className="text-xs text-white opacity-60">
                  <tr className="border-b border-[#151515]">
                    <th scope="col" className={`px-6 w-[20%] py-3`}>
                      <Typography text={'Profile'} size={'14px'} weight={400} />
                    </th>
                    <th scope="col" className={`px-6 w-[23%] py-3`}>
                      <Typography text={'User Id'} size={'14px'} weight={400} />
                    </th>
                    <th scope="col" className={`px-6 w-[15%] py-3`}>
                      <Typography text={'Investments'} size={'14px'} weight={400} />
                    </th>
                    <th scope="col" className={`px-6 w-[15%] py-3`}>
                      <Typography text={'Period'} size={'14px'} weight={400} />
                    </th>
                    <th scope="col" className={`px-6 w-[15%] py-3`}>
                      <Typography text={'Amount'} size={'14px'} weight={400} />
                    </th>
                    <th scope="col" className={`px-6 w-[13%] py-3`}>
                      <Typography text={'Payout'} size={'14px'} weight={400} />
                    </th>
                  </tr>
                </thead>
              </table>
              <div className="relative h-[516px] scrollbar-hide scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent overflow-y-auto overflow-x-auto" >
                <table className="w-full text-sm text-left rtl:text-right">
                  <tbody>
                    {(sortUsers(users) || []).map((user, index) => {
                      const userInvestments = investments?.filter((inv) => inv.userId === user._id) || [];
                      const totalUserInvestment = userInvestments.reduce((sum, inv) => sum + (inv.initialInvestment || 0), 0);
                      const totalUserPayout = userInvestments.reduce((sum, inv) => {
                        const payouts = inv.payouts || [];
                        return sum + payouts.reduce((pSum, payout) => pSum + (payout.amount || 0), 0);
                      }, 0);
                      const period = userInvestments.length > 0 ? userInvestments[0].term : 12; // Default to 12 months if no investments
                      return (
                        <tr
                          key={index}
                          onClick={() => setActiveIndex(index)}
                          className={`border-b py-4 border-[#151515] h-[50px] text-white ${index === activeIndex ? 'bg-[#DF0F11]' : ''}`}
                        >
                          <td className="px-6 w-[20%]">
                            <div className='flex items-center gap-[6px] '>
                              <img className="w-[26px] h-[26px] flex-shrink-0 rounded-[26px]" src={user?.avatar && user?.avatar !== '' ?  process.env.REACT_APP_BACKEND_URL+user?.avatar : admin_avatar} alt="admin_avatar" />
                              <Typography text={user.name} size={'14px'} weight={400} />
                            </div>
                          </td>
                          <td className="px-6 w-[23%]">
                            <Typography text={user._id} size={'14px'} weight={400} />
                          </td>
                          <td className="px-6 w-[15%]">
                            <Typography text={userInvestments.length} size={'14px'} weight={400} />
                          </td>
                          <td className="px-6 w-[15%]">
                            <Typography text={`${period} ${period === 1 ? 'Month' : 'Months'}`} weight={400} />
                          </td>
                          <td className="px-6 w-[15%]">
                            <Typography text={formatCurrency(totalUserInvestment)} size={'14px'} weight={400} />
                          </td>
                          <td className="px-6 w-[13%]">
                            <Typography text={formatCurrency(totalUserPayout)} size={'14px'} weight={400} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="lg:col-span-4 lg:gap-0 gap-5 lg:grid-cols-none md:grid-cols-2 grid-cols-1 lg:bg-black bg-opacity-0 bg-transparent lg:block grid justify-between col-span-12 lg:px-6">
          <div className='bg-black lg:p-0 p-5 rounded-[4px] col-span-1'>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Typography text={'Pending Approvals'} transform={'uppercase'} color={'#FFF'} size={'16px'} weight={400} lineHeight={'170%'} family={'Supply'} />
                <div className="w-4 h-4 rounded-full bg-[#DF0F11] flex items-center justify-center">
                  <Typography lineHeight={'100%'} size={'10.2px'} text={investments?.filter((inv) => inv.userId === users[activeIndex]?._id && inv.status === 'pending').length || 0} color={'#FFF'} />
                </div>
              </div>
              <Typography onClick={() => navigate(`/admin/investments`)} className="cursor-pointer" text={'VIEW ALL'} size={'12.052px'} textDecorationLine={'underline'} textDecorationStyle={'solid'} textDecorationSkipInk={'none'} textDecorationThickness={'auto'} textUnderlineOffset={'auto'} textUnderlinePosition={'from-font'} lineHeight={'170%'} family={'Supply'} weight={400} transform={'uppercase'} color={'#DF0F11'} />
            </div>
            <div className="flex justify-between mt-5 pb-4 border-b border-white border-opacity-10">
              <div className="flex items-center gap-6">
                <img alt='admin_avatar' width={48} height={48} src={process.env.REACT_APP_BACKEND_URL+users[activeIndex]?.avatar || admin_avatar} className="rounded w-12 h-12" />
                <div className="flex flex-col text-white">
                  <Typography size={'20px'} weight={500} lineHeight={'170%'} text={users[activeIndex]?.name || 'N/A'} />
                  <Typography size={'12px'} lineHeight={'170%'} text={`Joined on ${formatDate(users[activeIndex]?.createdAt)}`} />
                </div>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" className='mt-1' width="28" height="28" viewBox="0 0 28 28" fill="none">
                <mask id="mask0_498_856" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="28" height="28">
                  <rect width="28" height="28" fill="#DF0F11" />
                </mask>
                <g mask="url(#mask0_498_856)">
                  <path d="M14 26.1153L6.0935 20.1857C5.83178 19.9942 5.62576 19.7481 5.47546 19.4475C5.32515 19.1469 5.25 18.8305 5.25 18.4984V5.02576C5.25 4.4364 5.45417 3.93755 5.8625 3.52922C6.27083 3.12088 6.76968 2.91672 7.35904 2.91672H20.641C21.2303 2.91672 21.7292 3.12088 22.1375 3.52922C22.5458 3.93755 22.75 4.4364 22.75 5.02576V18.4984C22.75 18.8305 22.6748 19.1469 22.5245 19.4475C22.3742 19.7481 22.1682 19.9942 21.9065 20.1857L14 26.1153ZM12.775 17.0961L18.9627 10.9084L17.7333 9.64313L12.775 14.6015L10.3025 12.129L9.03729 13.3584L12.775 17.0961Z" fill="#DF0F11" />
                </g>
              </svg>
            </div>
            {(() => {
              const pendingInvestments = investments?.filter((inv) => inv.userId === users[activeIndex]?._id && inv.status === 'pending') || [];
              return pendingInvestments.length > 0 ? (
                <>
                  <div className="grid grid-cols-3 mt-4 pb-5 gap-7 border-b border-opacity-10 border-white">
                    <div className="col-span-1">
                      <Typography size={'12px'} color={'#DF0F11'} lineHeight={'170%'} text={'Investment amount'} />
                      <Typography size={'20px'} color={'#FFFFFF'} lineHeight={'170%'} text={formatCurrency(pendingInvestments.reduce((sum, inv) => sum + (inv.initialInvestment || 0), 0))} weight={500} family={'Supply'} />
                    </div>
                    <div className="col-span-1">
                      <Typography size={'12px'} color={'#DF0F11'} lineHeight={'170%'} text={'Period'} />
                      <Typography size={'20px'} color={'#FFFFFF'} lineHeight={'170%'} text={`${pendingInvestments[0]?.term || 12} months`} weight={500} family={'Supply'} />
                    </div>
                    <div className="col-span-1">
                      <Typography size={'12px'} color={'#DF0F11'} lineHeight={'170%'} text={'Target ROI'} />
                      <Typography size={'20px'} color={'#FFFFFF'} lineHeight={'170%'} text={`${pendingInvestments[0]?.annualROI || 0}%`} weight={500} family={'Supply'} />
                    </div>
                  </div>
                  <button className='bg-white rounded w-full mt-5 mb-4 px-[14px] py-[10px] h-8 flex justify-center items-center flex-shrink-0 gap-2'>
                    <Typography onClick={() => navigate(`/admin/review-sign/${pendingInvestments[0]?._id}`)} size={'12px'} color={'#000'} lineHeight={'170%'} text={'Review & Sign Contract'} />
                  </button>
                </>
              ) : (
                <div className='h-[20px]'></div>
              );
            })()}
          </div>
            <div className='col-span-1 bg-[#1B1B1B]' >
              <div className="bg-[#1B1B1B] h-auto border-t border-[#333] p-6">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Typography text={'New Tickets'} transform={'uppercase'} color={'#FFF'} size={'16px'} weight={400} lineheight={'170%'} family={'Supply'} />
                    <div className="w-4 h-4 rounded-full bg-[#DF0F11] flex items-center justify-center">
                      <Typography lineheight={0} size={'10.2px'} text={tickets?.filter((ticket) => ticket.status === 'Pending').length || 0} color={'#FFF'} />
                    </div>
                  </div>
                  <Typography onClick={() => navigate(`/admin/tickets`)} className="cursor-pointer" text={'VIEW ALL'} size={'12.052px'} textDecorationLine={'underline'} textDecorationStyle={'solid'} textDecorationSkipInk={'none'} textDecorationThickness={'auto'} textUnderlineOffset={'auto'} textUnderlinePosition={'from-font'} lineheight={'170%'} family={'Supply'} weight={400} transform={'uppercase'} color={'#DF0F11'} />
                </div>
                <div className="relative overflow-x-auto">
                  <table className="w-full text-sm text-left rtl:text-right table-fixed">
                    <thead className="text-xs text-white opacity-60">
                      <tr className="border-b border-white border-opacity-10">
                        <th scope="col" className={`w-[40%] py-3`}>
                          <Typography text={'User'} size={'14px'} weight={400} />
                        </th>
                        <th scope="col" className={`w-[40%] py-3`}>
                          <Typography text={'Query'} size={'14px'} weight={400} />
                        </th>
                        <th scope="col" className={`w-[20%] py-3`}>
                        </th>
                      </tr>
                    </thead>
                  </table>
                  <div className="relative max-h-48 scrollbar-hide scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent overflow-y-auto overflow-x-auto" >
                    <table className="w-full text-sm text-left rtl:text-right">
                      <tbody>
                        {(tickets || []).filter((ticket) => ticket.status === 'Pending').map((ticket, index) => {
                          const user = users.find(user => user._id === ticket.userId);
                          return (
                            <tr
                              key={index}
                              className={`border-b py-4 border-white border-opacity-10 h-[50px] text-white`}
                            >
                              <td className="w-[40%]">
                                <div className='flex items-center gap-[6px] '>
                                  <img className="w-[26px] h-[26px] flex-shrink-0 rounded-[26px]" src={user?.avatar && user?.avatar !== '' ?  process.env.REACT_APP_BACKEND_URL+user?.avatar : admin_avatar} alt="admin_avatar" />
                                  <Typography className='truncate whitespace-nowrap overflow-hidden text-ellipsis' text={user?.name || 'N/A'} size={'14px'} weight={400} />
                                </div>
                              </td>
                              <td className="w-[40%] truncate">
                                <Typography className='truncate whitespace-nowrap overflow-hidden text-ellipsis' text={ticket.query} size={'14px'} weight={400} />
                              </td>
                              <td className="w-[20%]">
                                <button onClick={() => navigate(`/admin/tickets/${ticket._id}`)} className='inline-flex px-[14px] py-[10px] rounded bg-[#DF0F11] text-white justify-center items-center flex-shrink-0 gap-2'>
                                  <Typography size={'12px'} lineheight={'170%'} text={'View'} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Accounts;